import {colors} from "@c10h/colors";
import {required} from "@components/Input/utils/Validators";
import SalesforceFormSuccess from "@components/Salesforce/SalesforceFormSuccess";
import {useTranslation} from "ni18n";
import React, {useMemo, useRef, useState} from "react";
import ReCAPTCHA from "react-google-recaptcha";
import {addToSalesforce} from "src/_services/api";
import {c4BLinkedInConversionId, dev} from "src/components/_common/_constants";
import {trackToPatient} from "src/components/_common/Carbon";
import {useTypedSelector} from "src/store";
import isEmail from "validator/lib/isEmail";

import {TextInput} from "../Input";
import Fade from "../Transitions/Fade";
import {Btn} from "../V5Comp";

type Props = {
  trackingCategory?: string;
  trackingAction?: string;
  companyFieldLabel?: string;
  channel?: string;
  submitButtonLabel?: string;
  emailFieldLabel?: string;
  titleFieldLabel?: string;
  excludedFields?: (keyof typeof initialFormState)[];
};

const initialFormState = {
  firstName: dev ? "carbon" : "",
  lastName: dev ? "eng" : "",
  title: dev ? "eng" : "",
  company: dev ? "carbon" : "",
  email: dev ? "eng@carbonhealth.com" : "",
};

const initialFormValidationState = {
  firstName: false,
  lastName: false,
  title: false,
  company: false,
  email: false,
};

type Fields = {
  name: keyof typeof initialFormState;
  label: string;
  defaultValue?: string;
  className?: string;
  errorMsg?: string;
};

export type SubmitStatus = "not-submitted" | "submitted" | "success" | "error";

const SalesforceForm = ({
  trackingCategory = "",
  trackingAction = "Lead Form",
  companyFieldLabel,
  channel = "",
  submitButtonLabel,
  emailFieldLabel,
  titleFieldLabel,
  excludedFields = [],
}: Props): JSX.Element => {
  const i18n = useTranslation();
  const [formState, setFormState] = useState(initialFormState);
  const [formValidationState, setFormValidationState] = useState(initialFormValidationState);
  const [submitStatus, setSubmitStatus] = useState<SubmitStatus>("not-submitted");
  // @ts-expect-error TS2345: Argument of type 'null' is not assignable to parameter of type 'number | (() => number)'.
  const [linkedInConversionId, setLinkedInConversionId] = useState<number>(null);

  const {recaptchaClientKey, csrfToken} = useTypedSelector(state => state.config);
  const recaptchaRef = useRef(null);

  const updateFormState = (key: keyof typeof initialFormState) => (val: string) => {
    setFormState({...formState, [key]: val});
  };

  const updateFormValidationState = (key: keyof typeof initialFormState) => (val: boolean) => {
    setFormValidationState({...formValidationState, [key]: val});
  };

  const onError = () => {
    setSubmitStatus("error");
  };

  const handleSubmit: React.FormEventHandler<HTMLFormElement> = async e => {
    e.preventDefault();
    if (isSubmitDisabled) {
      return;
    }

    setSubmitStatus("submitted");
    // @ts-expect-error TS2531: Object is possibly 'null'.
    const captcha = await recaptchaRef.current.executeAsync();
    addToSalesforce(csrfToken, {
      FirstName: formState.firstName,
      LastName: formState.lastName,
      Title: formState.title,
      Email: formState.email,
      Company: formState.company,
      LeadSource: "web",
      channel__c: channel,
      captcha,
    })
      .then(() => {
        trackToPatient({
          ga: {
            category: trackingCategory,
            action: trackingAction,
            label: formState.company,
          },
        });
        setLinkedInConversionId(c4BLinkedInConversionId);
        setSubmitStatus("success");
        setTimeout(() => {
          setSubmitStatus("not-submitted");
        }, 15000);
      })
      .catch(() => {
        onError();
      });
  };

  const isFormValid =
    !formState
      .omit("title")
      .vals()
      .map(val => Boolean(val))
      .includes(false) && isEmail(formState.email);

  const isSubmitDisabled = submitStatus === "submitted" || submitStatus === "error" || !isFormValid;

  const fields: Fields[] = useMemo(
    () => [
      {
        name: "firstName",
        label: i18n.t("First Name"),
        className: "w50p-f pr2 w100p-f-sm pr0-sm",
        defaultValue: initialFormState.firstName,
        errorMsg: formValidationState?.firstName ? required()(formState.firstName) : undefined,
      },
      {
        name: "lastName",
        label: i18n.t("Last Name"),
        className: "w50p-f pl2 w100p-f-sm pl0-sm",
        defaultValue: initialFormState.lastName,
        errorMsg: formValidationState?.lastName ? required()(formState.lastName) : undefined,
      },
      {
        name: "title",
        label: titleFieldLabel || i18n.t("Your title / position"),
        defaultValue: initialFormState.title,
        errorMsg: formValidationState?.title ? required()(formState.title) : undefined,
      },
      {
        name: "company",
        label: companyFieldLabel || i18n.t("Company name"),
        defaultValue: initialFormState.company,
        errorMsg: formValidationState?.company ? required()(formState.company) : undefined,
      },
      {
        name: "email",
        label: emailFieldLabel || i18n.t("Business email address"),
        defaultValue: initialFormState.email,
        errorMsg: formValidationState?.email ? required()(formState.email) : undefined,
      },
    ],
    [formValidationState, formState, companyFieldLabel, emailFieldLabel, titleFieldLabel, i18n],
  );

  return (
    <div className="bg-white br1 minw32 minw0-sm maxw32">
      {submitStatus === "success" || submitStatus === "error" ? (
        <SalesforceFormSuccess error={submitStatus === "error"} setSubmitStatus={setSubmitStatus} />
      ) : (
        <div className="p8 p4-md">
          <form id="salesforce-form" className="df fdc" onSubmit={handleSubmit}>
            <div className="">
              {fields.map(
                ({name, label, defaultValue, className, errorMsg}) =>
                  !excludedFields.includes(name) && (
                    <div key={name} className={`fl mb4 w100p ${className}`}>
                      <TextInput
                        label={`${i18n.t(label)} * `}
                        onChange={updateFormState(name)}
                        defaultValue={defaultValue}
                        errorMsg={errorMsg}
                        setValidation={updateFormValidationState(name)}
                      />
                    </div>
                  ),
              )}
            </div>

            {submitStatus === "submitted" ? (
              <Fade duration={100} in appear>
                <div className="fs20 font-cb lh17 mv3">{i18n.t("Saving your form...")}</div>
              </Fade>
            ) : (
              <Btn
                aria-label={i18n.t("Click to submit the form.")}
                className="focus-bsDarkBlue3 t-100 event-d2ybCl"
                bg={isSubmitDisabled ? colors.gray100 : colors.darkerYellow}
                color={isSubmitDisabled ? colors.gray400 : "#fff"}
                type="submit"
                disabled={isSubmitDisabled}
              >
                {submitButtonLabel || i18n.t("Submit Form")}
              </Btn>
            )}
          </form>
        </div>
      )}
      {/* @ts-expect-error TS2769: No overload matches this call. */}
      <ReCAPTCHA ref={recaptchaRef} sitekey={recaptchaClientKey} size="invisible" />
      {linkedInConversionId && (
        <img
          height="1"
          width="1"
          style={{display: "none"}}
          alt=""
          src={`https://px.ads.linkedin.com/collect/?pid=2265524&conversionId=${linkedInConversionId}&fmt=gif`}
        />
      )}
    </div>
  );
};

export default SalesforceForm;
